import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import { dayMonth } from '../commerce/standingApi';

/* Pausing a member's packs and training plans on their own (member modal).
   Each shows its service's pause rules; staff may pause past the limit or
   where pausing is off, with a reason. A pause that has not started can be
   withdrawn, a running one ended today — unused days come back off the end
   date when the pause moved it. Weekly slots follow automatically. */

interface Rules { enabled: boolean; maxTotalDays: number | null; requireReason: boolean; extendsValidity: boolean; memberCanPause: boolean }
interface PauseRow { id: string; startDate: string; endDate: string | null; phase: 'scheduled' | 'active' | 'completed'; reason: string | null; extendedDays: number; byMember: boolean }
interface Pack { kind: 'pack'; id: string; name: string; validTo: string | null; detail: string; rules: Rules; usedDays: number; remainingDays: number | null; weeklySlots: number; pauses: PauseRow[] }
interface Data { today: string; items: Pack[] }

async function api<T = any>(path: string, init: { method?: string; body?: any } = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch(`${API_BASE_URL}/api/pack-pauses${path}`, {
    method: init.method ?? 'GET',
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || 'Something went wrong');
  return json.data as T;
}

const addDays = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

function rulesText(p: Pack): string {
  if (!p.rules.enabled) return 'Pausing is off for this pack';
  const left = p.remainingDays == null ? 'no limit' : `${p.remainingDays} of ${p.rules.maxTotalDays} days left`;
  return `${left} · ${p.rules.extendsValidity ? 'moves the end date' : 'end date stays'}${p.rules.memberCanPause ? ' · members can pause' : ''}`;
}

export default function PackPausesPanel({ gymId, memberId, readOnly }: { gymId: string; memberId: string; readOnly: boolean }) {
  const [data, setData] = useState<Data | null>(null);
  const [form, setForm] = useState<{ pack: Pack; start: string; end: string; reason: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<Data>(`?gymId=${gymId}&memberId=${memberId}`).then(setData).catch(() => setData({ today: '', items: [] }));
  }, [gymId, memberId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<any>, ok: string) => {
    setSaving(true);
    try {
      await fn();
      toast.success(ok);
      setForm(null);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data || data.items.length === 0) return null;

  const length = form ? Math.round((Date.parse(form.end) - Date.parse(form.start)) / 86400000) + 1 : 0;
  const overLimit = !!form && (!form.pack.rules.enabled || (form.pack.remainingDays != null && length > form.pack.remainingDays));

  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Pause packs</p>
      {data.items.map(p => {
        const open = p.pauses.filter(x => x.phase !== 'completed');
        return (
          <div key={p.id} className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-800 truncate">{p.name}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {p.detail}{p.validTo ? ` · until ${dayMonth(p.validTo)}` : ''}{p.weeklySlots ? ` · ${p.weeklySlots} weekly slot${p.weeklySlots > 1 ? 's' : ''}` : ''}
                </p>
                <p className="text-[11px] text-slate-400">{rulesText(p)}</p>
              </div>
              {!readOnly && open.length === 0 && (
                <button
                  onClick={() => setForm({ pack: p, start: data.today, end: addDays(data.today, 6), reason: '' })}
                  className="shrink-0 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
                >
                  Pause
                </button>
              )}
            </div>
            {open.map(x => (
              <div key={x.id} className="flex items-center justify-between gap-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs font-bold text-sky-800">
                    {x.phase === 'active' ? 'Paused' : 'Pausing'} {dayMonth(x.startDate)} – {x.endDate ? dayMonth(x.endDate) : '…'}
                    {x.byMember ? ' · by the member' : ''}
                  </p>
                  <p className="text-[11px] text-sky-700 truncate">
                    {x.extendedDays ? `End date moved ${x.extendedDays} days` : 'End date unchanged'}{x.reason ? ` · ${x.reason}` : ''}
                  </p>
                </div>
                {!readOnly && (
                  x.phase === 'active' && x.startDate < data.today ? (
                    <button disabled={saving} onClick={() => run(() => api(`/${x.id}/resume`, { method: 'POST', body: { gymId } }), 'Pack resumed')} className="shrink-0 text-xs font-bold text-sky-800 hover:underline">
                      Resume today
                    </button>
                  ) : (
                    <button disabled={saving} onClick={() => run(() => api(`/${x.id}/cancel`, { method: 'POST', body: { gymId } }), 'Pause withdrawn')} className="shrink-0 text-xs font-bold text-sky-800 hover:underline">
                      Withdraw
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        );
      })}

      {form && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <p className="text-xs font-bold text-slate-700">Pause {form.pack.name}</p>
          <div className="flex flex-wrap gap-2">
            <label className="text-[11px] text-slate-500">From
              <input type="date" min={data.today} value={form.start} onChange={e => setForm({ ...form, start: e.target.value, end: form.end < e.target.value ? e.target.value : form.end })} className="block mt-0.5 px-2 py-1 border border-slate-300 rounded-lg text-sm" />
            </label>
            <label className="text-[11px] text-slate-500">To (inclusive)
              <input type="date" min={form.start} value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} className="block mt-0.5 px-2 py-1 border border-slate-300 rounded-lg text-sm" />
            </label>
          </div>
          <input
            value={form.reason}
            onChange={e => setForm({ ...form, reason: e.target.value })}
            placeholder={form.pack.rules.requireReason || overLimit ? 'Reason (required)' : 'Reason (optional)'}
            maxLength={300}
            className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-sm"
          />
          <p className="text-[11px] text-slate-500">
            {length} day{length === 1 ? '' : 's'}
            {form.pack.rules.extendsValidity && form.pack.validTo ? ` · ends ${dayMonth(addDays(form.pack.validTo, length))} instead of ${dayMonth(form.pack.validTo)}` : ''}
            {overLimit ? ' · overrides the pause rules' : ''}
          </p>
          <div className="flex gap-2">
            <button
              disabled={saving || length < 1}
              onClick={() => run(() => api('', { method: 'POST', body: { gymId, entitlementId: form.pack.id, startDate: form.start, endDate: form.end, reason: form.reason, override: overLimit } }), 'Pack paused')}
              className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Pause'}
            </button>
            <button onClick={() => setForm(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
